"use client"

import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

/**
 * Aligned with the hygenius-pria reference dialog
 * (../hygenius-pria/src/components/ui/dialog.tsx) so the PRIA feedback modal
 * and the rest of www share one primitive rather than a fork.
 *
 * What this replaced: a file pulled from a Tailwind-v4-era shadcn registry,
 * whose `rounded-xs` and `focus:outline-hidden` emit no CSS under www's
 * Tailwind 3, so the close button shipped with no radius and no outline reset.
 * Its parts were plain function components, which on React 18 drop a `ref`
 * instead of forwarding it; no call site passed one, so nothing was broken,
 * but the reference's forwardRef parts are the safe form and are what the rest
 * of components/shadcn/ui uses.
 *
 * That React-version gap is why every aligned primitive types its refs with
 * `React.ElementRef` where the reference uses the React 19 `React.ComponentRef`.
 * Both ship in www's @types/react 18, so this is a house-consistency choice, not
 * a forced one — expect it on every file in a mechanical diff against the
 * reference, and don't read it as drift.
 *
 * The differences that remain against the reference are each explained where
 * they appear, except the two on the close button: its outline reset and focus
 * ring are `focus-visible:`-scoped rather than the reference's `focus:`, because
 * .scratch/style-guide/STYLE-GUIDE.html requires focus rings to be focus-visible-scoped so they
 * don't fire on a mouse click, and its transition names the three properties
 * that actually change instead of `all`.
 *
 * The scrim then renders differently despite identical classes — www declares
 * `black` as #333, not #000, so the ported `bg-black/80` comes out very dark
 * grey. That is a token difference, not a code one; using www's own token beats
 * hardcoding a hex the design system doesn't define. If the scrim ever needs to
 * be true black, add the token rather than inlining the hex here.
 */

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    /** Classes for the dimmed backdrop, which is otherwise out of reach. */
    overlayClassName?: string
    /** Screen-reader name of the close button, for a localised caller. */
    closeLabel?: string
  }
>(({ className, overlayClassName, closeLabel = "Close", children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay className={overlayClassName} />
    {/*
      `@container/page` because a dialog portals out to <body>, where there is
      no page column to measure: without it the responsive classes on
      everything inside would never match. Declaring it here also makes them
      answer to the dialog's own width, which is what they were always after.
      Classes on this element itself still cannot be responsive — a container
      never queries itself — hence the flat `rounded-lg`.
      See `pageBreakpoints` in tailwind.config.js.
    */}
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "@container/page fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 rounded-lg",
        className
      )}
      {...props}
    >
      {children}
      <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm text-grey ring-offset-background transition-[color,border-radius,box-shadow] cursor-pointer hover:text-dark hover:rounded-md p-1.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
        <X className="h-4 w-4" />
        <span className="sr-only">{closeLabel}</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

/*
 * `min-[640px]:` rather than `sm:` on these two.
 *
 * DialogContent declares itself the `page` container so the *contents* a caller
 * puts inside reflow to the dialog. These two classes are not asking about the
 * dialog though — they ask whether the screen is a phone, which is the one
 * question the dialog cannot answer about itself: it caps at `max-w-lg`, 512px,
 * so `sm:` (640px) against its own width can never match and the footer would
 * stack its buttons vertically at every screen size.
 *
 * The arbitrary `min-[…]`/`max-[…]` variants are the ones tailwind.config.js
 * leaves measuring the window, so they restore exactly the pre-container
 * behaviour. Reach for them wherever a class inside a dialog means "the window",
 * not "the space I have".
 */
const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center min-[640px]:text-left",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse min-[640px]:flex-row min-[640px]:justify-end min-[640px]:space-x-2",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
